import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { GoldButton } from "../components/Buttons";
import { WhatsAppChatLink } from "../components/WhatsAppButton";
import Seo from "../components/Seo";
import { MaskedLines, Reveal, EASE } from "../components/Reveal";
import { CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEXT_STEPS = [
    "We reply and schedule a short consultation.",
    "We confirm what applies to you and what to have ready.",
    "You get a secure way to share documents — never this form.",
];

const ContactInfoRow = ({ icon: Icon, label, value, href, testid }) => (
    <a href={href} data-testid={testid} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined} className="group flex items-center gap-5 bg-white border border-[#E7E4DC] shadow-sm p-6 transition-all duration-300 hover:border-[#C8A45D]/70 hover:shadow-md">
        <span className="h-11 w-11 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-[#C8A45D]">
            <Icon className="h-4 w-4 text-[#8A6D2F] group-hover:text-[#111318] transition-colors duration-300" />
        </span>
        <span>
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#737985]">{label}</span>
            <span className="block text-[#20242B] text-base sm:text-lg mt-1">{value}</span>
        </span>
    </a>
);

const Field = ({ id, label, required, error, children }) => (
    <div className="space-y-2">
        <Label htmlFor={id} className="text-[12px] uppercase tracking-[0.16em] text-[#737985]">
            {label} {required ? <span className="text-[#8A6D2F]">*</span> : <span className="text-[#9AA0A6]">(optional)</span>}
        </Label>
        {children}
        {error ? (
            <p className="text-[#B4433A] text-xs leading-relaxed" data-testid={`error-${id}`}>{error}</p>
        ) : null}
    </div>
);

const SERVICE_OPTIONS = [
    "Individual Tax Return (W-2)",
    "Self-Employed / Gig Worker Taxes",
    "Small-Business Tax Services",
    "Multi-State Tax Filing",
    "Amended Tax Return",
    "IRS Notice Assistance",
    "Other",
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((p) => (p[key] ? { ...p, [key]: null } : p));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your full name.";
        if (!form.email.trim()) e.email = "Please enter your email address.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Please enter a valid email address, e.g. you@example.com.";
        if (!form.message.trim()) e.message = "Please briefly describe your tax situation.";
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Please fix the highlighted fields and try again.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(
                `${API}/inquiries`,
                {
                    inquiry_type: "individual",
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim() || null,
                    service: form.service || null,
                    message: form.message.trim(),
                },
                { timeout: 45000 },
            );
            setSubmitted(true);
            toast.success("Inquiry received — we'll be in touch shortly.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                const map = {};
                detail.forEach((d) => {
                    const f = d.loc?.[d.loc.length - 1];
                    if (typeof f === "string") map[f] = "Please check this field and try again.";
                });
                setErrors(map);
                toast.error("Please fix the highlighted fields and try again.");
            } else if (err?.response) {
                toast.error(
                    err.response.status >= 500
                        ? "We hit a snag on our side — please try again in a moment, or call us. Your details are still filled in."
                        : "Could not send your inquiry. Please check your details and try again, or call us."
                );
            } else {
                toast.error("Network issue — please check your connection and try again, or call us.");
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setForm({ name: "", email: "", phone: "", service: "", message: "" });
        setErrors({});
        setSubmitted(false);
    };

    return (
        <div data-testid="page-contact">
            <Seo
                title="Contact Us | Freedom Tax Filer — Chicago, IL"
                description="Talk to Freedom Tax Filer about your taxes: email support@freedomtaxfiler.com, message us on WhatsApp, or send an inquiry — 360 W Erie St, Chicago, IL."
                path="/contact"
            />
            <section className="relative pt-44 pb-14 bg-[#111318] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Contact Us</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-8 max-w-3xl">
                        <MaskedLines delay={0.2} lines={["Let's talk about", <>your <em className="italic text-gold-gradient">taxes.</em></>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-2xl">
                            Tell us a little about your situation and we will take it from there — a short note or a
                            call is all it takes.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 lg:pb-32" data-testid="contact-section">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    {/* Left: info */}
                    <div className="lg:col-span-5 space-y-5">
                        {CONTACT.phoneHref && (
                            <Reveal>
                                <ContactInfoRow icon={Phone} label="Call Us" value={CONTACT.phoneDisplay} href={CONTACT.phoneHref} testid="contact-phone-link" />
                            </Reveal>
                        )}
                        <Reveal delay={0.06}>
                            <ContactInfoRow icon={Mail} label="Email" value={CONTACT.email} href={CONTACT.emailHref} testid="contact-email-link" />
                        </Reveal>
                        <Reveal delay={0.09}>
                            <ContactInfoRow icon={MapPin} label="Our Location" value={CONTACT.address} href={CONTACT.mapsHref} testid="contact-maps-link" />
                        </Reveal>
                        <Reveal delay={0.11}>
                            <WhatsAppChatLink testid="contact-whatsapp-link" className="mt-2 w-full sm:w-auto">Chat With Us on WhatsApp</WhatsAppChatLink>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="bg-white border border-[#E7E4DC] shadow-sm p-7" data-testid="contact-next-steps">
                                <p className="eyebrow">What Happens Next</p>
                                <ul className="mt-6 space-y-4">
                                    {NEXT_STEPS.map((s, i) => (
                                        <li key={s} className="flex items-start gap-4 text-[#4A4F58] text-sm leading-relaxed">
                                            <span className="font-display text-[#8A6D2F] text-lg leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="border border-[#C8A45D]/40 bg-[#C8A45D]/[0.08] p-7 flex items-start gap-4" data-testid="contact-privacy-note">
                                <ShieldCheck className="h-6 w-6 text-[#8A6D2F] shrink-0 mt-0.5" />
                                <p className="text-[#4A4F58] text-sm leading-relaxed">
                                    <span className="text-[#20242B] font-semibold">A note on privacy:</span> please don't send tax
                                    documents, Social Security numbers, or account details through this form. It's for general
                                    inquiries only — we arrange secure document delivery after you become a client.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="bg-white border border-[#E7E4DC] shadow-xl p-7 sm:p-10" data-testid="contact-form-card">
                                {submitted ? (
                                    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="text-center py-14" data-testid="form-success">
                                        <CheckCircle2 className="h-14 w-14 text-[#C8A45D] mx-auto" />
                                        <h2 className="font-display text-3xl text-[#20242B] mt-6">Inquiry received.</h2>
                                        <p className="text-[#737985] leading-relaxed mt-4 max-w-md mx-auto">
                                            Thank you — your tax inquiry is on its way. We will reach out using the
                                            contact details you provided.
                                        </p>
                                        <GoldButton className="mt-9" onClick={reset} data-testid="form-success-again">
                                            Send Another Inquiry <ArrowRight className="h-4 w-4" />
                                        </GoldButton>
                                    </motion.div>
                                ) : (
                                    <>
                                        <div className="border-b border-[#E7E4DC] pb-7">
                                            <p className="eyebrow">Individual Tax Clients</p>
                                            <h2 className="font-display text-2xl sm:text-3xl text-[#20242B] mt-3">Send your inquiry.</h2>
                                            <p className="text-[#737985] text-sm mt-2">A person reads every message — no bots, no queues.</p>
                                        </div>

                                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="inquiry-form" noValidate>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Field id="name" label="Full Name" required error={errors.name}>
                                                    <Input id="name" required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Jane Doe" aria-invalid={!!errors.name} className="input-dark" data-testid="input-name" />
                                                </Field>
                                                <Field id="email" label="Email Address" required error={errors.email}>
                                                    <Input id="email" type="email" required autoComplete="email" autoCapitalize="none" value={form.email} onChange={set("email")} placeholder="you@example.com" aria-invalid={!!errors.email} className="input-dark" data-testid="input-email" />
                                                </Field>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Field id="phone" label="Phone Number" error={errors.phone}>
                                                    <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" maxLength={40} value={form.phone} onChange={set("phone")} placeholder="Your phone number (optional)" className="input-dark" data-testid="input-phone" />
                                                </Field>
                                                <Field id="service" label="Tax Service Needed" error={errors.service}>
                                                    <select id="service" value={form.service} onChange={set("service")} className="input-dark" data-testid="select-service">
                                                        <option value="">Select a service…</option>
                                                        {SERVICE_OPTIONS.map((o) => (
                                                            <option key={o} value={o}>{o}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                            </div>

                                            <Field id="message" label="Tell Us About Your Tax Situation" required error={errors.message}>
                                                <Textarea id="message" required maxLength={5000} rows={6} value={form.message} onChange={set("message")} placeholder="Briefly describe the tax assistance you need." aria-invalid={!!errors.message} className="input-dark h-auto py-3 resize-y" data-testid="textarea-message" />
                                            </Field>

                                            <div className="pt-2">
                                                <GoldButton type="submit" disabled={loading} className="w-full sm:w-auto disabled:opacity-50" data-testid="submit-inquiry">
                                                    {loading ? "Submitting…" : "Submit My Inquiry"}
                                                    <ArrowRight className="h-4 w-4" />
                                                </GoldButton>
                                                <p className="text-[#9AA0A6] text-xs leading-relaxed mt-5" data-testid="form-privacy-note">
                                                    By submitting this form, you agree that Freedom Tax Filer may contact you
                                                    regarding your inquiry. Please do not include Social Security numbers, tax
                                                    identification numbers, passwords, or sensitive tax documents in this form.
                                                </p>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
